/**
 * Pure logic of the "Product ABC" dialog (port of AbcProductComponent helpers):
 * form values <-> product props, threads split by type, payload.
 */
import type { Product, Thread } from './api';

export const VIEWS = ['main', 'custom', 'custom2', 'custom3'] as const;
export type View = (typeof VIEWS)[number];

export interface ViewValues {
  scale: number;
  x: number;
  y: number;
}

export type AbcValues = Record<View, ViewValues>;

/** Missing views leave their fields empty (NaN). */
export const productToAbc = (product: Product): AbcValues => {
  const of = (view: View): ViewValues => {
    const props = product.imageProps?.[view];
    return {
      scale: props?.scale ?? Number.NaN,
      x: props?.offsetX ?? Number.NaN,
      y: props?.offsetY ?? Number.NaN,
    };
  };
  return {
    main: of('main'),
    custom: of('custom'),
    custom2: of('custom2'),
    custom3: of('custom3'),
  };
};

export const EMPTY_ABC: AbcValues = {
  main: { scale: 100, x: 0, y: 0 },
  custom: { scale: 100, x: 0, y: 0 },
  custom2: { scale: 100, x: 0, y: 0 },
  custom3: { scale: 100, x: 0, y: 0 },
};

/** An empty scale means 100, an empty offset 0. */
export const abcToImageProps = (values: AbcValues) =>
  Object.fromEntries(
    VIEWS.map((view) => [
      view,
      {
        scale: Number.isNaN(values[view].scale) ? 100 : values[view].scale,
        offsetX: Number.isNaN(values[view].x) ? 0 : values[view].x,
        offsetY: Number.isNaN(values[view].y) ? 0 : values[view].y,
      },
    ]),
  ) as Record<View, { scale: number; offsetX: number; offsetY: number }>;

export const validateAbc = (
  values: AbcValues,
): Partial<Record<View, string>> => {
  const errors: Partial<Record<View, string>> = {};
  VIEWS.forEach((view) => {
    const scale = values[view].scale;
    if (!Number.isNaN(scale) && (scale < 0 || scale > 1000))
      errors[view] = 'Wrong scale';
  });
  return errors;
};

export interface Color {
  hex: string;
  name: string;
  type: string;
}

const toColor = (thread: Thread): Color => ({
  hex: thread.hex,
  name: thread.name ?? '',
  type: thread.type ?? '',
});

/** Threads of the product split into the two lists of the dialog. */
export const splitThreads = (
  threads: readonly Thread[] | null | undefined,
): { iconic: Color[]; exclusive: Color[] } => {
  const iconic: Color[] = [];
  const exclusive: Color[] = [];
  (threads ?? []).forEach((thread) => {
    (thread.type === 'iconic' ? iconic : exclusive).push(toColor(thread));
  });
  return { iconic, exclusive };
};

/** The threads not yet in either list, with the label of the selector. */
export const unselectedThreads = (
  threads: readonly Thread[],
  iconic: readonly Color[],
  exclusive: readonly Color[],
): (Color & { label: string })[] =>
  threads
    .filter(
      (thread) =>
        !iconic.some((color) => color.hex === thread.hex) &&
        !exclusive.some((color) => color.hex === thread.hex),
    )
    .map((thread) => ({
      ...toColor(thread),
      label: `${thread.name} - ${thread.hex} (${thread.type})`,
    }));

/** The list without the colour at `index`. */
export const removeColor = (list: readonly Color[], index: number): Color[] =>
  list.filter((_, i) => i !== index);

export const moveColor = (
  list: readonly Color[],
  index: number,
  delta: number,
): Color[] => {
  if (index + delta < 0 || index + delta >= list.length) return [...list];
  const next = [...list];
  const [color] = next.splice(index, 1);
  next.splice(index + delta, 0, color as Color);
  return next;
};

/** Text props of the product without its threads. */
export const specialPositionProps = (
  product: Product,
): Record<string, unknown> => {
  const rest = Object.fromEntries(
    Object.entries(product.textProps ?? {}).filter(
      ([key]) => key !== 'threads',
    ),
  );
  return structuredClone(rest);
};

export interface TextPropsValues {
  scale: number;
  rotate: number;
  curve: number;
  letterSpace: number;
  offsetX: number;
  offsetY: number;
}

export type SpecialPositionValues = Record<
  'custom' | 'custom2',
  TextPropsValues
>;

const DEFAULTS: TextPropsValues = {
  scale: 100,
  rotate: 0,
  curve: 0,
  letterSpace: -1,
  offsetX: 0,
  offsetY: 0,
};
export const toSpecialPositionValues = (
  textProps: Record<string, unknown> | null | undefined,
): SpecialPositionValues => {
  const of = (key: 'custom' | 'custom2'): TextPropsValues => {
    const props = (textProps?.[key] ?? {}) as Partial<
      Record<keyof TextPropsValues, number | null>
    >;
    return {
      scale: props.scale ?? DEFAULTS.scale,
      rotate: props.rotate ?? DEFAULTS.rotate,
      curve: props.curve ?? DEFAULTS.curve,
      letterSpace: props.letterSpace ?? DEFAULTS.letterSpace,
      offsetX: props.offsetX ?? DEFAULTS.offsetX,
      offsetY: props.offsetY ?? DEFAULTS.offsetY,
    };
  };
  return { custom: of('custom'), custom2: of('custom2') };
};
